import { useEffect, useState } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, PosButton, Segmented } from '@/components/ui'
import { cn } from '@/lib/utils'
import { discountAmount } from '@/services'
import { DiscountCap, withinCap } from '@/services/PricingService'
import { PosModel } from '@/types'
import { formatMoney } from '@/utils'

import { NumPad } from './NumPad'

interface DiscountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** The amount the discount applies to, for the live preview and the cap. */
  base: number
  current?: PosModel.Discount
  onApply: (discount?: PosModel.Discount) => void
  /** The cashier's `max_discount` (§7.2); a discount above it is refused. */
  cap?: DiscountCap | null
}

const PRESETS = [5, 10, 15, 20]

export const DiscountDialog = ({ open, onOpenChange, title, base, current, onApply, cap }: DiscountDialogProps) => {
  const [type, setType] = useState<PosModel.DiscountType>('percent')
  const [value, setValue] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!open) return
    setType(current?.type ?? 'percent')
    setValue(current ? String(current.value) : '')
    setReason(current?.reason ?? '')
  }, [open, current])

  const n = parseFloat(value) || 0
  const overCap = !withinCap(base, { type, value: n }, cap ?? null)
  const invalid = (type === 'percent' ? n > 100 : n > base) || overCap
  const off = discountAmount(base, { type, value: n })

  const apply = () => {
    if (invalid) return
    onApply(n > 0 ? { type, value: n, reason: reason.trim() || undefined } : undefined)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), apply())}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>On {formatMoney(base)}</DialogDescription>
        </DialogHeader>

        <Segmented
          className="w-full"
          value={type}
          onChange={(t) => {
            setType(t)
            setValue('')
          }}
          options={[
            { value: 'percent', label: 'Percent %' },
            { value: 'fixed', label: 'Amount ৳' },
          ]}
        />

        {type === 'percent' && (
          <div className="grid grid-cols-4 gap-2">
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setValue(String(p))}
                className={cn('h-10 rounded-md border text-sm font-medium', n === p ? 'border-primary bg-primary text-white' : 'border-input hover:bg-subdued')}
              >
                {p}%
              </button>
            ))}
          </div>
        )}

        <div className="space-y-1.5">
          <Input
            autoFocus
            inputMode="decimal"
            value={value}
            aria-invalid={invalid}
            onChange={(e) => setValue(e.target.value.replace(/[^\d.]/g, ''))}
            placeholder={type === 'percent' ? '0 %' : '৳0.00'}
            className="h-12 text-right text-xl font-semibold tabular-nums"
          />
          <p className={cn('text-right text-xs', invalid ? 'text-error' : 'text-muted-foreground')}>
            {overCap
              ? `Above your limit of ${cap!.type === 'percentage' ? `${cap!.value}%` : formatMoney(cap!.value)}`
              : invalid
                ? 'More than the amount it applies to'
                : off > 0 ? `Saves ${formatMoney(off)} · new ${formatMoney(base - off)}` : ' '}
          </p>
        </div>

        <NumPad value={value} onChange={setValue} />

        <div className="space-y-1.5">
          <Label htmlFor="discount-reason">Reason (optional)</Label>
          <Input id="discount-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Damaged pack, loyalty, manager OK…" />
        </div>

        <DialogFooter>
          {current && (
            <PosButton variant="ghost" className="mr-auto text-error" onClick={() => (onApply(undefined), onOpenChange(false))}>
              Remove discount
            </PosButton>
          )}
          <PosButton variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </PosButton>
          <PosButton onClick={apply} disabled={invalid}>
            Apply
          </PosButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
